// src/components/layout/Header.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { getEmojiAvatar, toDisplayName } from '../../utils/avatarHelpers';
import { useAuthStore } from '../../store/authStore';

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  height: 60px;
  background: var(--tg-theme-bg-color, #ffffff);
  border-bottom: 1px solid rgba(123, 104, 238, 0.1);
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(123, 104, 238, 0.2);
  background: linear-gradient(135deg, rgba(123, 104, 238, 0.12), rgba(153, 102, 204, 0.12));
  transition: all 0.2s ease;
  cursor: pointer;
  color: #7B68EE; /* Светлая тема: text-purple-600 */

  /* ✅ Защита от глобальных стилей и псевдоэлементов */
  line-height: 0;
  font-size: 0;

  /* ✅ Убираем возможные псевдоэлементы */
  &::before,
  &::after {
    content: none !important;
    display: none !important;
  }

  /* ✅ Брендовый пурпурный цвет для иконки + явный показ SVG */
  svg {
    color: currentColor;
    stroke: currentColor !important;
    fill: none !important;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: inline-block !important;
    visibility: visible !important;
  }

  svg path,
  svg polyline,
  svg line {
    stroke: currentColor !important;
    fill: none !important;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  /* ✅ Доступность: видимый focus ring с контрастом ≥ 4.5:1 */
  &:focus-visible {
    outline: 2px solid #7B68EE;
    outline-offset: 2px;
  }

  &:hover {
    background: linear-gradient(135deg, rgba(123, 104, 238, 0.2), rgba(153, 102, 204, 0.2));
    border-color: rgba(123, 104, 238, 0.3);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  /* Темная тема: text-purple-400 */
  [data-theme="dark"] & {
    color: #C4B5FD;
  }
`;

const Title = styled(motion.h1)`
  font-size: 18px;
  font-weight: 700;
  color: var(--tg-theme-text-color, #000000);
  text-align: center;
  flex: 1;
  margin: 0 16px;
`;

const Logo = styled(motion.div)`
  font-size: 20px;
  font-weight: 800;
  background: var(--gradient-logo);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
`;

const UserClickableArea = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(123, 104, 238, 0.1);
    transform: scale(1.02);
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const Avatar = styled.div<{ $hasPhoto?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$hasPhoto
    ? 'transparent'
    : 'var(--gradient-purple)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px; /* ✅ увеличен для эмодзи */
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(123, 104, 238, 0.3);
  overflow: hidden;

  [data-theme="dark"] & {
    box-shadow: 0 2px 8px rgba(157, 142, 238, 0.4);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserName = styled.span`
  font-size: 14px;
  color: var(--tg-theme-text-color, #000000);
  font-weight: 600;
  display: none;
  
  @media (min-width: 400px) {
    display: block;
  }
`;

interface HeaderProps {
  currentPath: string;
  onBack: () => void;
  onProfileClick: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  '/': 'GELTEK',
  '/calendar': 'GELTEK',
  '/welcome': 'Добро пожаловать',
  '/questionnaire': 'Анкета',
  '/achievements': 'Достижения',
  '/profile': 'Профиль',
};

export default function Header({
  currentPath,
  onBack,
  onProfileClick,
}: HeaderProps) {
  // ✅ получаем пользователя из стора
  const { user } = useAuthStore();

  // ✅ Состояние для fallback при ошибке загрузки фото
  const[photoError, setPhotoError] = React.useState(false);

  // 🚑 Если бекенд начал отдавать валидное фото после ошибки, пробуем загрузить снова
  React.useEffect(() => {
    if (user?.photo_url) {
      console.log('📷 Photo URL изменился:', user.photo_url);
      setPhotoError(false);
    }
  }, [user?.photo_url]);

  const isHomePage = currentPath === '/' || currentPath === '/calendar';
  const shouldShowBackButton = !isHomePage && currentPath !== '/welcome';

  const getPageTitle = () => {
    for (const[path, title] of Object.entries(PAGE_TITLES)) {
      if (currentPath.startsWith(path)) {
        return title;
      }
    }
    return 'GELTEK';
  };

  // ✅ Нормализация имени пользователя строго из БД
  // Приоритет: first_name + last_name → username → "Гость"
  // Игнорируем Telegram initDataUnsafe.user — БД единственный источник правды
  const avatarEmoji = getEmojiAvatar(user?.telegram_id);
  const hasPhotoUrl = Boolean(user?.photo_url) && !photoError;
  const displayName = toDisplayName(user);

  return (
    <HeaderContainer>
      {/* Кнопка "Назад" или пустое пространство для выравнивания */}
      {shouldShowBackButton ? (
        <BackButton
          onClick={onBack}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
          aria-label="Назад"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </BackButton>
      ) : (
        <div style={{ width: 40 }} />
      )}

      {/* Заголовок */}
      {isHomePage ? (
        <Logo
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          GELTEK
        </Logo>
      ) : (
        <Title
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {getPageTitle()}
        </Title>
      )}

      {/* Профиль */}
      <UserClickableArea
        onClick={onProfileClick}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
        aria-label="Открыть профиль"
      >
        {/* ✅ Показываем первое слово из нормализованного имени */}
        {displayName && <UserName>{displayName.split(' ')[0]}</UserName>}

        {/* ✅ Показываем фото из API если есть, иначе эмодзи */}
        <Avatar $hasPhoto={hasPhotoUrl}>
          {hasPhotoUrl ? (
            <img
              src={user?.photo_url || ''}
              alt={displayName}
              onError={() => setPhotoError(true)}
            />
          ) : (
            avatarEmoji
          )}
        </Avatar>
      </UserClickableArea>
    </HeaderContainer>
  );
}
